import {
  createWalletClient,
  custom,
  Hex,
  hexToBigInt,
  http,
  PrivateKeyAccount,
  toBytes,
  toHex,
} from 'viem';
import { BUNDLER_URL, CHAIN, PAYMASTER_URL } from './constants';
import { createPimlicoClient } from 'permissionless/clients/pimlico';
import { createSmartAccountClient } from 'permissionless';
import { toSafeSmartAccount } from 'permissionless/accounts';
import { ConnectedWallet } from '@privy-io/react-auth';
import { TSmartAccountClient } from '@/types';
import { publicClient } from './contract-utils';
import { generatePrivateKey } from 'viem/accounts';
import { entryPoint07Address } from 'viem/account-abstraction';

export const pimlicoClient = createPimlicoClient({
  transport: http(PAYMASTER_URL),
  entryPoint: {
    address: entryPoint07Address,
    version: '0.7',
  },
});

export const signerToSmartAccount = async (signer: PrivateKeyAccount) =>
  await toSafeSmartAccount({
    client: publicClient,
    owners: [signer],
    entryPoint: {
      address: entryPoint07Address,
      version: '0.7',
    },
    version: '1.4.1',
  });

export const getSmartAccountClient = async (
  signer: PrivateKeyAccount
): Promise<TSmartAccountClient> => {
  const smartAccount = await signerToSmartAccount(signer);

  return createSmartAccountClient({
    account: smartAccount,

    chain: CHAIN,
    paymaster: pimlicoClient,
    bundlerTransport: http(BUNDLER_URL),
    userOperation: {
      estimateFeesPerGas: async () =>
        (await pimlicoClient.getUserOperationGasPrice()).fast,
    },
  });
};

export const privyWalletToClient = async (wallet: ConnectedWallet) => {
  const eip1193Provider = await wallet.getEthereumProvider();
  return createWalletClient({
    account: wallet.address as `0x${string}`,
    chain: CHAIN,
    transport: custom(eip1193Provider),
  });
};

export const waitForUserOpReceipt = async (
  hash: Hex,
  timeout = DEFAULT_TIMEOUT
) => {
  const receipt = await pimlicoClient.waitForUserOperationReceipt({
    hash,
    timeout,
  });

  if (!receipt.success)
    throw new Error(`Failed to execute user operation: ${receipt.reason}`);
  return receipt;
};

export const getNonce = () =>
  hexToBigInt(
    toHex(toBytes(generatePrivateKey()).slice(0, 24), {
      size: 32,
    })
  );

export const getUserOpStatus = async (hash: Hex) => {
  const receipt = await pimlicoClient.getUserOperationStatus({
    hash,
  });
  return receipt.status;
};

export const FAILED_USER_OP_STATUSES = [
  'not_found',
  'not_submitted',
  'rejected',
  'reverted',
  'failed',
];

export const FILE_TRX_TIMEOUT = 30000; // 30 seconds
export const PORTAL_MINT_TIMEOUT = 90000; // 90 seconds
export const DEFAULT_TIMEOUT = 60000; // 1 minute
export const PENDING_TXN_TIMEOUT = 15000; // 15 seconds
